import { useState, useCallback, useEffect } from 'react';
import { Edit3, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface ContentTitleEditableProps {
  title: string;
  placeholder?: string;
  onSave: (newTitle: string) => void;
  className?: string;
  disabled?: boolean;
  maxLength?: number;
}

export function ContentTitleEditable({
  title,
  placeholder = "Click to add title",
  onSave,
  className,
  disabled = false,
  maxLength = 200
}: ContentTitleEditableProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const [isSaving, setIsSaving] = useState(false);

  // Update local state when title prop changes
  useEffect(() => {
    setEditValue(title);
  }, [title]);

  const handleEdit = useCallback(() => {
    if (disabled) return;
    setIsEditing(true);
    setEditValue(title);
  }, [disabled, title]);

  const handleSave = useCallback(async () => {
    const trimmedValue = editValue.trim();
    
    // Don't save if value hasn't changed
    if (trimmedValue === title) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save title:', error);
      // Keep editing mode open on error
    } finally {
      setIsSaving(false);
    }
  }, [editValue, title, onSave]);

  const handleCancel = useCallback(() => {
    setEditValue(title);
    setIsEditing(false);
  }, [title]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  }, [handleSave, handleCancel]);

  if (isEditing) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={maxLength}
          className="flex-1"
          autoFocus
          disabled={isSaving}
        />
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={handleSave}
            disabled={isSaving || editValue.trim() === title}
            className="h-8 w-8 p-0"
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("group inline-flex items-center gap-2 cursor-pointer", className)}>
      <div 
        className="min-w-0"
        onClick={handleEdit}
      >
        {title ? (
          <p className="font-medium">{title}</p>
        ) : (
          <p className="text-muted-foreground italic">{placeholder}</p>
        )}
      </div>
      {!disabled && (
        <Button
          size="sm"
          variant="ghost"
          onClick={handleEdit}
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Edit3 className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}
